import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { CHEAT_TYPES, GAME_MODES } from "../lib/constants";
import { Upload, Loader2, Check, X, MinusCircle, ShieldCheck } from "lucide-react";

const selectCls = "rounded-lg border border-[#1E293F] bg-[#141B2D] px-3 py-2 text-sm text-white focus:border-[#00F0FF]/40 focus:outline-none";

export const BulkImportPanel = () => {
  const [platform, setPlatform] = useState("Steam");
  const [cheatType, setCheatType] = useState("Other");
  const [gameMode, setGameMode] = useState("REC");
  const [status, setStatus] = useState("confirmed_cheater");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const handles = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!handles.length) return toast.error("Paste at least one profile (one per line).");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/bulk-import", {
        platform, handles, cheat_type: cheatType, game_mode: gameMode, status,
      });
      setResult(data);
      toast.success(`Imported ${data.created} · skipped ${data.skipped} · failed ${data.failed}.`);
      if (data.created > 0) setText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6" data-testid="bulk-import-panel">
      <div className="flex items-center gap-2">
        <Upload className="text-[#00F0FF]" size={20} />
        <h3 className="font-heading text-lg font-black tracking-tight text-white">Bulk Import Known Cheaters</h3>
      </div>
      <p className="mt-1 text-sm text-[#94A3B8]">
        Paste a list of profiles — <span className="text-white">one per line</span>. For Steam use a profile link / custom URL / SteamID; for Xbox use the exact gamertag. We auto-lock each permanent ID so renames are tracked from day one.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs text-[#64748B]">Platform
          <select data-testid="bulk-platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className={selectCls}>
            <option value="Steam">Steam</option>
            <option value="Xbox">Xbox</option>
            <option value="PS5">PlayStation</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[#64748B]">Offense
          <select data-testid="bulk-cheat" value={cheatType} onChange={(e) => setCheatType(e.target.value)} className={selectCls}>
            {CHEAT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[#64748B]">Mode
          <select data-testid="bulk-mode" value={gameMode} onChange={(e) => setGameMode(e.target.value)} className={selectCls}>
            {GAME_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[#64748B]">Post as
          <select data-testid="bulk-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
            <option value="confirmed_cheater">Confirmed Cheater</option>
            <option value="suspect">Suspect</option>
          </select>
        </label>
      </div>

      <Textarea
        data-testid="bulk-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder={"gabelogannewell\nhttps://steamcommunity.com/id/someone\n76561198000000000"}
        className="mt-3 border-[#1E293F] bg-[#141B2D] font-mono text-sm text-white placeholder:text-[#64748B]"
      />

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-[#64748B]">Up to 100 at a time.</span>
        <Button data-testid="bulk-import-btn" onClick={run} disabled={busy} className="bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload size={15} className="mr-2" />} Import
        </Button>
      </div>

      {result && (
        <div className="mt-4 space-y-2 rounded-lg border border-[#1E293F] bg-[#141B2D] p-3 text-sm" data-testid="bulk-result">
          <div className="flex flex-wrap gap-3 font-semibold">
            <span className="text-[#00FF87]">✓ {result.created} imported</span>
            <span className="text-[#FFB800]">— {result.skipped} skipped</span>
            <span className="text-[#FF2A55]">✕ {result.failed} failed</span>
          </div>
          <ul className="max-h-52 space-y-1 overflow-y-auto text-xs">
            {result.results.created.map((r, i) => (
              <li key={`c${i}`} className="flex items-center gap-1.5 text-[#86EFAC]">
                <Check size={12} /> <span className="font-semibold text-white">{r.gamertag}</span>
                {r.locked ? <span className="inline-flex items-center gap-0.5 text-[#00FF87]"><ShieldCheck size={11} /> ID locked</span> : <span className="text-[#64748B]">no ID lock</span>}
              </li>
            ))}
            {result.results.skipped.map((r, i) => (
              <li key={`s${i}`} className="flex items-center gap-1.5 text-[#FCD34D]"><MinusCircle size={12} /> {r.handle} — {r.reason}</li>
            ))}
            {result.results.failed.map((r, i) => (
              <li key={`f${i}`} className="flex items-center gap-1.5 text-[#FF8FA3]"><X size={12} /> {r.handle} — {r.error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
